export const RESET_PAGE_LIMIT = 1000;

// Reset flows can delete very large sets (especially logs). Launching every
// mutation in a single Promise.allSettled can crash the browser tab ("Render
// process gone"). Keep outbound concurrency bounded.
export const RESET_DELETE_CONCURRENCY = 20;

export const RESET_GRAPHQL_MAX_ATTEMPTS = 5;
export const RESET_GRAPHQL_BASE_BACKOFF_MS = 250;

const RESET_LIST_FILTER_FIELDS = {
  listLogs: "tenantId",
  listAnnouncements: "tenantId",
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const flattenGraphqlError = (reason) => {
  if (!reason) return "Unknown error";

  if (typeof reason === "string") return reason;

  const directMessage =
    typeof reason.message === "string" && reason.message.trim().length > 0
      ? reason.message
      : null;

  const graphQLErrors = Array.isArray(reason.errors) ? reason.errors : null;
  const graphQLMessages = graphQLErrors
    ? graphQLErrors
        .map((e) => e?.message)
        .filter((m) => typeof m === "string" && m.trim().length > 0)
    : [];

  if (graphQLMessages.length > 0) {
    return graphQLMessages.join(" | ");
  }

  if (directMessage) return directMessage;

  try {
    return JSON.stringify(reason);
  } catch {
    return String(reason);
  }
};

const isRetryableGraphqlFailureText = (text) => {
  const t = String(text || "").toLowerCase();
  if (!t) return false;

  return (
    t.includes("throttl") ||
    t.includes("timeout") ||
    t.includes("timed out") ||
    t.includes("too many requests") ||
    t.includes("limit exceeded") ||
    t.includes("throughput") ||
    t.includes("provisioned") ||
    t.includes("service unavailable") ||
    t.includes("bad gateway") ||
    t.includes("gateway timeout") ||
    t.includes("network error") ||
    t.includes("failed to fetch")
  );
};

export const graphqlOpWithRetry = async (
  operation,
  {
    maxAttempts = RESET_GRAPHQL_MAX_ATTEMPTS,
    baseBackoffMs = RESET_GRAPHQL_BASE_BACKOFF_MS,
  } = {}
) => {
  let lastErrorText = "Unknown error";

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await operation();
    } catch (err) {
      lastErrorText = flattenGraphqlError(err);

      const retryable = isRetryableGraphqlFailureText(lastErrorText);
      if (!retryable || attempt === maxAttempts) {
        const wrapped = new Error(lastErrorText);
        wrapped.cause = err;
        throw wrapped;
      }

      const backoff =
        baseBackoffMs > 0 ? baseBackoffMs * 2 ** (attempt - 1) : 0;
      if (backoff > 0) {
        await sleep(backoff);
      }
    }
  }

  const wrapped = new Error(lastErrorText);
  throw wrapped;
};

export const buildResetListVariables = (
  responseKey,
  reloadYardId,
  nextToken = null
) => {
  const filterField = RESET_LIST_FILTER_FIELDS[responseKey] || "reloadYardId";

  return {
    filter: { [filterField]: { eq: reloadYardId } },
    limit: RESET_PAGE_LIMIT,
    nextToken,
  };
};

export const runPromisesInConcurrencyChunks = async (
  tasks,
  concurrency = RESET_DELETE_CONCURRENCY
) => {
  const results = [];

  for (let i = 0; i < tasks.length; i += concurrency) {
    const chunk = tasks.slice(i, i + concurrency);
    results.push(
      ...(await Promise.allSettled(
        chunk.map((task) => graphqlOpWithRetry(() => task()))
      ))
    );
  }

  return results;
};
